import React, { useEffect, useState, useCallback } from 'react';
import { ReactComponent as DocumentIcon } from '../../../../assets/icons/document.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/export.svg';
import Button from '../../../../composants/Button/Button';
import SectionPliable from '../../../../composants/SectionPliable/SectionPliable';
import { BlocLiensConsultation } from '../../liensConsultation';
import { formatDate } from '../../../../utils/entityUtils';
import { getParisLabel } from '../../../../utils/timezone';
import '../../../../composants/DocumentsList/DocumentsList.css';
import './TabAvisDocuments.css';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccordCadreInfo {
  id: number;
  nom: string;
  acheteur?: string;
  reference_avis?: string;
  expire_at?: string;
  nombre_lots_techniques?: number;
  budget_estime?: number;
  source_financement?: string;
  type_marche_ccag?: string;
  categorie_cpv?: string;
  date_limite_candidature?: string;
  langue?: string;
  delai_validite_offre?: string;
  presentation_consultation?: string;
}

interface DocumentAC {
  id: number;
  type: string;
  label: string;
  filename: string;
  mime_type: string;
  filesize: number;
  uploaded_at: string;
}

interface TabAvisDocumentsProps {
  accordCadre: AccordCadreInfo;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDateFr = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatBudget = (value?: number): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

// ── Composant ─────────────────────────────────────────────────────────────────

const TabAvisDocuments: React.FC<TabAvisDocumentsProps> = ({ accordCadre }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  const [documents, setDocuments] = useState<DocumentAC[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [loadingDocId, setLoadingDocId] = useState<number | null>(null);

  // Modale preview
  const [previewDoc, setPreviewDoc] = useState<DocumentAC | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  // ── Chargement des documents ──────────────────────────────────────────────

  const loadDocuments = useCallback(() => {
    if (!accordCadre.id) return;
    setLoadingDocs(true);
    setDocsError(null);

    fetch(`${apiUrl}/publications/ac27/accords-cadres/${accordCadre.id}/documents`, {
      headers: authHeaders,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        return res.json();
      })
      .then((json) => setDocuments(json.data ?? []))
      .catch((err) => setDocsError(err.message))
      .finally(() => setLoadingDocs(false));
  }, [accordCadre.id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Bloquer le scroll quand la modale est ouverte
  useEffect(() => {
    if (previewBlobUrl) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [previewBlobUrl]);

  // ── Fetch binaire du document ────────────────────────────────────────────

  const fetchDocBlob = async (doc: DocumentAC): Promise<Blob> => {
    const res = await fetch(
      `${apiUrl}/publications/ac27/accords-cadres/${accordCadre.id}/documents/${doc.id}/download`,
      { headers: authHeaders }
    );
    if (!res.ok) throw new Error(`Erreur ${res.status}`);
    return res.blob();
  };

  // ── Preview (clic sur la carte = ouvre la modale) ────────────────────────

  const handlePreview = async (doc: DocumentAC) => {
    setLoadingDocId(doc.id);
    try {
      const blob = await fetchDocBlob(doc);
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
      setPreviewDoc(doc);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocId(null);
    }
  };

  // ── Telechargement ───────────────────────────────────────────────────────

  const handleDownload = async (doc: DocumentAC) => {
    setLoadingDocId(doc.id);
    try {
      const blob = await fetchDocBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `document-${doc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocId(null);
    }
  };

  const closeModal = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewDoc(null);
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Section 1 : Informations de l'avis */}
      <SectionPliable title="Avis de marché">
      <div className="section-content">
        {/* Les avis officiels et le profil acheteur se consultent hors Centralis :
            ils sont en tete de section, avant les caracteristiques de l'avis. */}
        <BlocLiensConsultation />
        <div className="avis-grid">
          <div className="avis-field">
            <label>Reference de l'avis</label>
            <span>{accordCadre.reference_avis || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Acheteur</label>
            <span>{accordCadre.acheteur || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Type de marche / CCAG</label>
            <span>{accordCadre.type_marche_ccag || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Categorie CPV</label>
            <span>{accordCadre.categorie_cpv || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Budget estime</label>
            <span>{formatBudget(accordCadre.budget_estime)}</span>
          </div>
          <div className="avis-field">
            <label>Source de financement</label>
            <span>{accordCadre.source_financement || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Date limite de candidature</label>
            <span>{formatDateTime(accordCadre.date_limite_candidature)} <small style={{ color: '#6b7280', fontSize: '0.8em' }}>({getParisLabel()})</small></span>
          </div>
          <div className="avis-field">
            <label>Nombre de lots techniques</label>
            <span>{accordCadre.nombre_lots_techniques ?? '-'}</span>
          </div>
          <div className="avis-field">
            <label>Langue</label>
            <span>{accordCadre.langue || '-'}</span>
          </div>
          <div className="avis-field">
            <label>Delai de validite des offres</label>
            <span>{accordCadre.delai_validite_offre || '-'}</span>
          </div>
          {accordCadre.presentation_consultation && (
            <div className="avis-field" style={{ gridColumn: '1 / -1' }}>
              <label>Présentation de la consultation</label>
              <span style={{ whiteSpace: 'pre-wrap' }}>{accordCadre.presentation_consultation}</span>
            </div>
          )}
        </div>
      </div>
      </SectionPliable>

      {/* Section 2 : Documents de l'accord-cadre */}
      <SectionPliable title="Documents de consultation">
      <div className="section-content">
        {loadingDocs && (
          <div className="docs-loading">
            <div className="spinner" />
            <span>Chargement des documents...</span>
          </div>
        )}

        {docsError && (
          <div className="docs-error">
            Impossible de charger les documents.
          </div>
        )}

        {!loadingDocs && !docsError && documents.length === 0 && (
          <div className="docs-empty">
            Aucun document de consultation disponible.
          </div>
        )}

        {!loadingDocs && !docsError && documents.length > 0 && (
          <div className="documents-list">
            <div className="documents-grid documents-grid-inline">
              {documents.map((doc) => {
                const isLoading = loadingDocId === doc.id;
                return (
                  <div
                    key={doc.id}
                    className="document-card document-card-inline"
                    onClick={() => handlePreview(doc)}
                  >
                    {isLoading ? (
                      <div className="document-loading-centered">
                        <div className="spinner"></div>
                      </div>
                    ) : (
                      <div className="document-left">
                        <DocumentIcon className="document-icon" />
                        <div className="document-info">
                          <span className="document-type">{doc.label}</span>
                          <span className="document-date">
                            {doc.filename}
                          </span>
                          <span className="document-date">
                            Mise en ligne le {formatDate(new Date(doc.uploaded_at))}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </SectionPliable>

      {/* Modale preview PDF */}
      {previewBlobUrl && previewDoc && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
            <h3>{previewDoc.label}</h3>
            <iframe
              src={previewBlobUrl}
              title={previewDoc.label}
              width="100%"
              height="500px"
            />
            <div className="modal-actions">
              <Button
                onClick={() => handleDownload(previewDoc)}
                icon={<DownloadIcon className="dl-button-icon-white" />}
                value="Telecharger"
                size="small"
              />
              <Button
                onClick={closeModal}
                value="Fermer"
                size="small"
                variant="secondary"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TabAvisDocuments;
